import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';
import { FormComponentProps } from '../types';
import { RadioButtonExpander } from './RadioButtonExpander';

interface FormRadioButtonProps<T extends FieldValues> extends FormComponentProps<T> {
  title: string;
  description?: string;
  value: string;
  group: string;
  setValue: UseFormSetValue<T>;
  fieldValue: PathValue<T, Path<T>>;
  expanded?: boolean;
  setExpanded?: () => void;
  buttonClassName?: string;
}

export const FormRadioButton = <T extends FieldValues>(props: FormRadioButtonProps<T>) => {
  const isReadonly = !!props.readonly;
  const isChecked = props.fieldValue === props.value;
  const inputId = `${props.group}-${props.value}`;

  const handleSelect = () => {
    if (isReadonly) return;
    props.setValue(props.name, props.value as any, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    if (props.setExpanded) {
      props.setExpanded();
    }
  };

  const radioBase = 'translate-y-1 w-4 h-4 border-[2px] rounded-full before:w-2 before:h-2 before:rounded-full before:scale-0 before:shadow-radio-button checked:before:scale-[1px]';

  const radioReadonly = 'bg-secondary-deep-blue-70 border-secondary-deep-blue-30';

  const radioEditable = 'bg-interactive-bold-green-db-12 border-secondary-deep-blue-30 hover:border-solid-bold-green checked:border-solid-bold-green';

  return (
    <div className={`rounded-md px-2 py-2 transition', ${isReadonly ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`} aria-disabled={isReadonly}>
      <div className={`flex flex-row w-full items-center justify-between h-6  ${props.buttonClassName || ''}`}>
        <div
          className="flex flex-row items-start"
          onClick={() => {
            if (isReadonly) return;
            handleSelect();
          }}
        >
          <input
            id={inputId}
            value={props.value}
            onChange={handleSelect}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSelect();
              }
            }}
            disabled={isReadonly}
            checked={isChecked}
            type="radio"
            className={`${radioBase}  ${isReadonly ? radioReadonly : radioEditable}`}
          />

          <label htmlFor={inputId} className={`ml-5 mr-1 flex items-center gap-2 select-none ${isReadonly ? 'text-secondary-deep-blue-20' : ''}`}>
            <span>{props.title}</span>
          </label>
        </div>
        <div className="flex items-center justify-center">{props.setExpanded && <RadioButtonExpander isExpanded={!!props.expanded} onClick={props.setExpanded} />}</div>
      </div>

      {props.description !== undefined && (
        <span
          className={`block whitespace-pre-wrap mt-3 mb-2 ml-8 mr-8 leading-6 text-sm text-secondary-deep-blue-20 overflow-hidden transition-[max-height] ease-in duration-300 ${props.expanded ? 'max-h-40' : 'max-h-0'}`}
        >
          {props.description}
        </span>
      )}
    </div>
  );
};
